import { readonly, ref } from 'vue';
import UploadedFile from './UploadedFile';
import { useI18n } from 'vue-i18n';

export function useFileManager(amountLimit: number, fileSizeLimit: number, videoFileSizeLimit: number, videoDurationLimit: number, allowedFileTypes: string[]) {
  const error = ref<Record<string, string | null>>({});
  const { t } = useI18n();

  const uploadedFiles = ref<Record<string, UploadedFile[]>>({});

  function keyExists(key: string): boolean {
    return key in uploadedFiles.value;
  }

  function fileExists(key: string, id: string): boolean {
    return uploadedFiles.value[key].some((file) => file.id === id);
  }

  function isTypeAllowed(type: string): boolean {
    return allowedFileTypes.includes(type);
  }

  function isUnderAmountLimit(key: string): boolean {
    return uploadedFiles.value[key].length < amountLimit;
  }

  function isUnderFileSizeLimit(fileSize: number): boolean {
    return fileSize < fileSizeLimit;
  }

  function isUnderVideoFileSizeLimit(fileSize: number): boolean {
    return fileSize < videoFileSizeLimit;
  }

  function isVideoFile(fileType: string): boolean {
    return fileType.includes('video');
  }

  function isUnderVideoDurationLimit(videoDuration: number): boolean {
    return videoDuration < videoDurationLimit;
  }

  function clearErrors(key: string): void {
    error.value[key] = null;
  }

  async function addFiles(key: string, files: File[]): Promise<void> {
    clearErrors(key);

    for (const file of files) {
      const uploadedFile = new UploadedFile(file);

      if (!keyExists(key)) {
        uploadedFiles.value[key] = [];
      }

      if (fileExists(key, uploadedFile.id)) {
        continue;
      }

      if (!isUnderAmountLimit(key)) {
        error.value[key] = t('customer.file-manager:errors:amount-limit', { amount: amountLimit });
        continue;
      }

      if (!isTypeAllowed(uploadedFile.type)) {
        error.value[key] = t('customer.file-manager:errors:type-not-allowed', { fileName: uploadedFile.name });
        continue;
      }

      if (!isUnderFileSizeLimit(uploadedFile.size) && !isVideoFile(uploadedFile.type)) {
        error.value[key] = t('customer.file-manager:errors:file-size-limit', { fileName: uploadedFile.name, size: fileSizeLimit / (1024 * 1024) });
        continue;
      }

      if (isVideoFile(uploadedFile.type)) {
        if (!isUnderVideoFileSizeLimit(uploadedFile.size)) {
          error.value[key] = t('customer.file-manager:errors:video-file-size-limit', { fileName: uploadedFile.name, size: videoFileSizeLimit / (1024 * 1024) });
          continue;
        }

        if (!isUnderVideoDurationLimit(await uploadedFile.getDuration())) {
          error.value[key] = t('customer.file-manager:errors:video-duration-limit', { fileName: uploadedFile.name, duration: videoDurationLimit });
          continue;
        }
      }


      uploadedFiles.value[key].push(uploadedFile);
    }
  }

  function removeFile(key: string, id: string): void {
    uploadedFiles.value[key] = uploadedFiles.value[key].filter((file) => file.id !== id);

    clearErrors(key);
  }

  function clearFiles(): void {
    uploadedFiles.value = {};
  }

  function setError(key: string, message: string): void {
    error.value[key] = message;
  }

  return {
    addFiles,
    error: readonly(error),
    uploadedFiles,
    removeFile,
    clearFiles,
    setError,
  };
}
